import React, { useCallback } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import VBox from '@/components/layout/VBox';
import PageHeader from '@/components/layout/PageHeader';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiLoading, mdiMagnify, mdiPencil, mdiPlus, mdiHistory, mdiDotsVertical, mdiFlaskOutline, mdiRobotOutline, mdiRobotOffOutline, mdiShieldSync, mdiShieldCheck, mdiShieldOff, mdiShieldAlert, mdiUpdate, mdiDownload, mdiChartLine, mdiChip, mdiMemory, mdiHarddisk, mdiLanConnect, mdiRouterWireless, mdiSwapHorizontal, mdiAlertCircleOutline, mdiClockAlertOutline, mdiPackageUp } from '@mdi/js';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { extractApiError } from '@/lib/apiError';
import { useDebounce } from '@/lib/useDebounce';
import { toastError, toastSuccess, toastWarning } from '@/lib/toast';
import Modal from '@/components/layout/Modal';
import { FormAppliance } from './FormAppliance';
import { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import { TelemetryCurrentResourceDTO } from '@webip/shared/src/dto/telemetry';
import SyncLogModal from './SyncLogModal';
import DryRunModal, { DryRunAcao } from './DryRunModal';
import DiagnosticsModal from './DiagnosticsModal';
import UpgradeModal from './UpgradeModal';
import BotaoExcluirComCascata from '@/components/BotaoExcluirComCascata';
import BotaoDropdown from '@/components/BotaoDropdown';
import AgentDownloadModal from './AgentDownloadModal';
import InstallAgentModal from './InstallAgentModal';
import useWindowSize from '@/lib/useWindowSize';
import {
    telemetryStatusMeta,
    getTelemetryTone,
    getUsagePercent,
    formatPercent,
    formatUsagePercent,
    formatUptime,
    formatCollectedAtShort,
} from '@/lib/telemetryFormat';
import TelemetryOverviewModal from '@/components/TelemetryOverviewModal';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import type { OsPatchApplianceSummaryDTO, OsPatchSeverityDTO } from '@webip/shared/src/dto/os-patch';
import type { ApplianceUpgradeDTO } from '@webip/shared/src/dto/appliance_upgrade';
import { BrandIcon } from '@/components/brandIcons/BrandIcon';

type ResponseList = {
    status: number;
    ok?: boolean;
    result: ApplianceDTO[];
    total: number;
    cursor: string | number | null;
};

type AgentStatus = {
    agentId: string;
    version: string;
    agentType: 'mikrotik' | 'opnsense';
    capabilities: string[];
    installType?: string;
    connectedAt: string;
    squidStatus?: { status: string; checkedAt: string };
};

/** Espelha ipfix.IpfixSourceStats do go-agent-core, com o applianceId já
 *  resolvido pelo backend (ver D-ipfix-limite). */
type IpfixApplianceStats = {
    applianceId: number;
    receivedPerMin: number;
    droppedPerMin: number;
    receivedTotal: number;
    droppedTotal: number;
    /** Cap em vigor no coletor (datagramas/s); null se o agente ainda não reporta. */
    maxPps: number | null;
    updatedAt: string;
    /** Desvio do relógio do router (s) medido pelos fluxos; positivo = atrasado. null sem fluxo recente. */
    clockSkewSec: number | null;
};

/** Acima disto o desvio do relógio do router vira alerta — espelha ROUTER_CLOCK_SKEW_WARN_SEC do backend. */
const ROUTER_CLOCK_SKEW_WARN_SEC = 60 * 60;

/** Versão mínima do RouterOS aceita pelo sync — espelha routerOSMin* do adapter-mk. */
const ROUTEROS_MIN: [number, number] = [7, 17];

/** true se a versão ("7.16.2", "7.18beta2") é reconhecível e menor que min. */
function routerosAbaixoDe(version: string, [major, minor]: [number, number]): boolean {
    const m = /^(\d+)\.(\d+)/.exec(version.trim());
    if (!m) return false;
    const [ma, mi] = [Number(m[1]), Number(m[2])];
    return ma < major || (ma === major && mi < minor);
}

function formatSkew(sec: number): string {
    const abs = Math.abs(sec);
    const txt = abs >= 86400 ? `${(abs / 86400).toFixed(1)} d`
        : abs >= 3600 ? `${(abs / 3600).toFixed(1)} h`
            : `${Math.round(abs / 60)} min`;
    return `${txt} ${sec > 0 ? 'atrasado' : 'adiantado'}`;
}

const applianceTypeMeta = {
    mikrotik: {
        label: 'MikroTik',
        color: 'var(--tc-text-color-info-emphasis)',
        background: 'var(--tc-background-color-info-subtle)',
    },
    opnsense: {
        label: 'OPNSense',
        color: 'var(--tc-text-color-warning-emphasis)',
        background: 'var(--tc-background-color-warning-subtle)',
    },
} as const;

// Metadados de severidade de patches — mesma tabela da página de dispositivos
// (pages/dispositivos/page.tsx), troca de severidade igual nos dois lugares.
const SEVERITY_META: Record<OsPatchSeverityDTO, { label: string; variant: BadgeVariants }> = {
    critical: { label: 'crítico', variant: 'danger' },
    high: { label: 'alto', variant: 'danger' },
    medium: { label: 'médio', variant: 'warning' },
    low: { label: 'baixo', variant: 'secondary' },
    unknown: { label: 'não classificado', variant: 'secondary' },
};

const SEVERITY_ORDER: OsPatchSeverityDTO[] = ['critical', 'high', 'medium', 'low', 'unknown'];

const Page = () => {
    const [, , isMobile] = useWindowSize();
    const [search, setSearch] = React.useState<string>('');
    const [model, setModel] = React.useState<ApplianceDTO | null>(null);
    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [syncLogAppliance, setSyncLogAppliance] = React.useState<ApplianceDTO | null>(null);
    const [dryRunAppliance, setDryRunAppliance] = React.useState<ApplianceDTO | null>(null);
    const [dryRunLoading, setDryRunLoading] = React.useState(false);
    const [dryRunAcoes, setDryRunAcoes] = React.useState<DryRunAcao[]>([]);
    const [dryRunErro, setDryRunErro] = React.useState<string | null>(null);
    const [agentDownloadOpen, setAgentDownloadOpen] = React.useState(false);
    const [installAgentOpen, setInstallAgentOpen] = React.useState(false);
    const [telemetryAppliance, setTelemetryAppliance] = React.useState<ApplianceDTO | null>(null);
    const [diagnosticsAppliance, setDiagnosticsAppliance] = React.useState<ApplianceDTO | null>(null);
    const [upgradeAppliance, setUpgradeAppliance] = React.useState<ApplianceDTO | null>(null);

    const debouncedSearch = useDebounce(search, 500);

    const queryAgents = useQuery({
        queryKey: ['agents'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agents: AgentStatus[] }>('/api/appliance/agents', { withCredentials: true });
            return res.data.agents;
        },
        refetchInterval: 10_000,
    });

    const queryLatestVersion = useQuery({
        queryKey: ['agent-latest-version'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; version: string | null }>('/api/agent/release', { withCredentials: true });
            return res.data.version;
        },
        refetchInterval: 300_000,
    });

    const queryTelemetryCurrent = useQuery({
        queryKey: ['telemetry-appliances-current'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; items: TelemetryCurrentResourceDTO[] }>('/api/telemetry/appliances/current', {
                withCredentials: true,
            });
            return res.data.items;
        },
        refetchInterval: 60_000,
    });

    const queryIpfixStats = useQuery({
        queryKey: ['ipfix-stats'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; items: IpfixApplianceStats[] }>('/api/appliance/ipfix-stats', {
                withCredentials: true,
            });
            return res.data.items;
        },
        refetchInterval: 60_000,
    });

    // Patches das appliances (scan periódico feito pelo adapter, reportado via
    // WS) — leitura passiva, sem botão de "checar agora".
    const queryOsPatchesAppliances = useQuery({
        queryKey: ['os-patches-appliances-current'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; items: OsPatchApplianceSummaryDTO[] }>('/api/os-patches/appliances/current', {
                withCredentials: true,
            });
            return res.data.items;
        },
        refetchInterval: 300_000,
    });

    // Atualizações do sistema (RouterOS/OPNsense) agendadas/em execução — badge na coluna Nome.
    const queryUpgradesAtivos = useQuery({
        queryKey: ['appliance-upgrades-ativos'],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; items: ApplianceUpgradeDTO[] }>('/api/appliance/upgrades/ativos', {
                withCredentials: true,
            });
            return res.data.items;
        },
        // Rápido só com upgrade em execução (etapa muda a cada passo); sem nenhum
        // ou só agendados, no ritmo das outras consultas da tela — um upgrade
        // agendado por outra sessão ainda aparece sem recarregar a página.
        refetchInterval: (data) =>
            data?.some((u) => u.status === 'executando' || u.status === 'reiniciando') ? 15_000 : 60_000,
    });

    const upgradeMap = React.useMemo(() => {
        const map = new Map<number, ApplianceUpgradeDTO>();
        for (const item of queryUpgradesAtivos.data ?? []) map.set(item.appliance_id, item);
        return map;
    }, [queryUpgradesAtivos.data]);

    const mutationAgentUpdate = useMutation<{ ok: boolean }, unknown, number>(
        async (applianceId: number) => {
            const res = await axios.post<{ ok: boolean }>(`/api/appliance/${applianceId}/agent/update`, {}, { withCredentials: true });
            return res.data;
        },
        {
            onSuccess: () => {
                // O agente decide sozinho se há atualização (verifica o manifest
                // assinado); isso só pede pra ele checar agora em vez de esperar.
                toastSuccess('Checagem de atualização solicitada ao agente');
            },
            onError: (err) => {
                const { message } = extractApiError(err, 'Erro ao solicitar atualização');
                toastError(message);
            },
        },
    );

    const agentMap = React.useMemo(() => {
        const map = new Map<string, AgentStatus>();
        for (const a of queryAgents.data ?? []) map.set(a.agentId, a);
        return map;
    }, [queryAgents.data]);

    const telemetryMap = React.useMemo(() => {
        const map = new Map<number, TelemetryCurrentResourceDTO>();
        for (const item of queryTelemetryCurrent.data ?? []) {
            map.set(item.resource_id, item);
        }
        return map;
    }, [queryTelemetryCurrent.data]);

    const ipfixStatsMap = React.useMemo(() => {
        const map = new Map<number, IpfixApplianceStats>();
        for (const item of queryIpfixStats.data ?? []) {
            map.set(item.applianceId, item);
        }
        return map;
    }, [queryIpfixStats.data]);

    const appliancePatchMap = React.useMemo(() => {
        const map = new Map<number, OsPatchApplianceSummaryDTO>();
        for (const item of queryOsPatchesAppliances.data ?? []) {
            map.set(item.appliance_id, item);
        }
        return map;
    }, [queryOsPatchesAppliances.data]);

    const telemetryTotals = React.useMemo(() => {
        const base = { online: 0, degraded: 0, offline: 0, unknown: 0 };
        for (const item of queryTelemetryCurrent.data ?? []) {
            base[item.status] += 1;
        }
        return base;
    }, [queryTelemetryCurrent.data]);

    const queryList = useInfiniteQuery({
        queryKey: ['appliance', debouncedSearch],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();

            if (debouncedSearch) {
                params.append('txt', debouncedSearch);
            }

            if (pageParam) {
                params.append('c', pageParam);
            }

            const response = await axios.get<ResponseList>(
                `/api/appliance?${params.toString()}`,
                {
                    withCredentials: true,
                },
            );
            return response;
        },
        getNextPageParam: lastPage => {
            return lastPage.data.cursor ?? undefined;
        },
    });

    const mutDelete = useMutation({
        mutationFn: async (id: number) => {
            return await axios.delete(`/api/appliance/${id}`, {
                withCredentials: true,
            });
        },
        onSuccess: () => {
            queryList.refetch();
            toastSuccess('Appliance removida com sucesso!');
        },
        onError: (error: unknown) => {
            const { message } = extractApiError(error, 'Erro ao remover appliance');
            toastError(message);
        },
    });

    const mutSave = useMutation({
        mutationFn: async ({ model, id }: { model: ApplianceDTO; id: number | null }) => {
            if (id != null) {
                return await axios.put(`/api/appliance/${id}`, model, {
                    withCredentials: true,
                });
            } else {
                return await axios.post('/api/appliance', model, {
                    withCredentials: true,
                });
            }
        },
        onSuccess: (response, variables) => {
            queryList.refetch();
            const savedAppliance = (response?.data as { result?: ApplianceDTO } | undefined)?.result;
            const isCreate = variables.id == null;
            if (isCreate && (!savedAppliance?.portas || savedAppliance.portas.length === 0)) {
                toastWarning(
                    `Appliance criada, mas nenhuma porta foi detectada — o agente "${savedAppliance?.agent_id}" não respondeu. Verifique se ele está conectado e alcança o equipamento, e salve a appliance novamente antes de configurar WAN/LAN.`,
                    { autoClose: 15000 },
                );
            } else {
                toastSuccess('Appliance salva com sucesso!');
            }
            setIsModalOpen(false);
            setModel(null);
            setEditingId(null);
            setFieldErrors({});
        },
        onError: (error: unknown) => {
            const { message, fieldErrors: errs } = extractApiError(error, 'Erro ao salvar appliance');
            if (errs) setFieldErrors(errs);
            // fieldErrors alimenta o texto inline sob cada campo, mas alguns erros
            // (ex: agent_host, que vive na tela de Agentes) não têm campo correspondente
            // neste formulário — sem isso o usuário via só a mensagem genérica no toast
            // e precisava abrir o dev tools pra achar o motivo real.
            const detalhes = errs ? Object.values(errs).join(' ') : '';
            toastError(detalhes ? `${message} ${detalhes}` : message);
        },
    });

    const handleOpenModal = useCallback((appliance?: ApplianceDTO) => {
        if (appliance) {
            setModel({ ...appliance });
            setEditingId(appliance.id ?? null);
        } else {
            setModel({
                agent_type: 'mikrotik',
                nome: '',
                descricao: '',
                enderecos: [''],
                usuario: '',
                senha: '',
                snmp_enabled: false,
                snmp_version: '2c',
                snmp_port: 161,
                snmp_community: '',
                redesLan: [],
                redesWan: [],
                ipsecs: [],
                dispositivos: [],
                portas: [],
            } as ApplianceDTO);
            setEditingId(null);
        }
        setFieldErrors({});
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setModel(null);
        setEditingId(null);
        setFieldErrors({});
    }, []);

    const handleSave = useCallback(() => {
        if (!model) return;
        // Na criação o agente é obrigatório — ele é instalado antes, em
        // Agentes, e a appliance só o referencia. O backend recusa do mesmo
        // jeito; validar aqui evita a ida ao servidor e deixa o erro no campo.
        if (editingId == null && !model.agent_id) {
            setFieldErrors(errs => ({
                ...errs,
                agent_id: 'Selecione o agente que vai operar esta appliance',
            }));
            return;
        }
        mutSave.mutate({ model, id: editingId });
    }, [model, mutSave, editingId]);

    const handleDelete = useCallback((id: number) => {
        mutDelete.mutate(id);
    }, [mutDelete]);

    const handleDryRun = useCallback(async (appliance: ApplianceDTO) => {
        setDryRunAppliance(appliance);
        setDryRunLoading(true);
        setDryRunAcoes([]);
        setDryRunErro(null);
        try {
            const response = await axios.post(
                `/api/appliance/${appliance.id}/sync-dryrun`,
                {},
                { withCredentials: true }
            );
            setDryRunAcoes(response.data.acoes ?? []);
        } catch (error: unknown) {
            const { message } = extractApiError(error, 'Erro ao simular sincronização');
            setDryRunErro(message);
        } finally {
            setDryRunLoading(false);
        }
    }, []);

    // Badge passivo de patches por appliance — nenhum botão, nenhuma ação:
    // o adapter escaneia sozinho (PATCH_SCAN_INTERVAL_SECONDS) e o backend
    // reconcilia; aqui só lemos o resumo.
    const renderPatchesCell = useCallback((row: ApplianceDTO) => {
        const summary = row.id != null ? appliancePatchMap.get(row.id) : undefined;
        if (!summary || !summary.last_scan_at) {
            return <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-300)' }}>sem varredura</span>;
        }

        if (summary.scan_error) {
            return (
                <span
                    style={{ fontSize: '12px', color: 'var(--tc-color-warning-500, #b45309)' }}
                    title={summary.scan_error}
                >
                    erro no scan
                </span>
            );
        }

        if (summary.missing_count === 0) {
            return <Badge variant="success">Em dia</Badge>;
        }

        // v1: RouterOS/OPNsense não têm fonte pública de severidade — nunca
        // inventamos uma; mostramos a contagem sem badge de severidade (mesmo
        // tratamento do macOS na página de dispositivos). Quando o catálogo
        // passar a classificar, worst != 'unknown' cai no badge colorido.
        const worst = SEVERITY_ORDER.find((sev) => summary.by_severity[sev] > 0) ?? 'unknown';
        if (worst === 'unknown') {
            return (
                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                    {summary.missing_count} pendente{summary.missing_count !== 1 ? 's' : ''} · severidade não disponível
                </span>
            );
        }

        const meta = SEVERITY_META[worst];
        return (
            <Badge variant={meta.variant}>
                {summary.by_severity[worst]} {meta.label}{summary.by_severity[worst] !== 1 ? 's' : ''}
            </Badge>
        );
    }, [appliancePatchMap]);

    const renderTelemetryCell = useCallback((row: ApplianceDTO) => {
        const telemetry = row.id != null ? telemetryMap.get(row.id) : undefined;
        const collectedAtText = formatCollectedAtShort(telemetry?.collected_at);
        const cpuTone = getTelemetryTone(telemetry?.current?.cpu_percent);
        const memoryPercent = getUsagePercent(telemetry?.current?.memory_used_bytes, telemetry?.current?.memory_total_bytes);
        const memoryTone = getTelemetryTone(memoryPercent);
        const diskPercent = getUsagePercent(telemetry?.current?.disk_used_bytes, telemetry?.current?.disk_total_bytes);
        const diskTone = getTelemetryTone(diskPercent);
        const routerosVersion = typeof telemetry?.metadata?.routeros_version === 'string' ? telemetry.metadata.routeros_version : null;
        const routerosAntigo = routerosVersion != null && routerosAbaixoDe(routerosVersion, ROUTEROS_MIN);

        return (
            <VBox style={{ gap: '4px', minWidth: 0, width: '100%' }}>
                <HBox style={{ gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        color: cpuTone.color,
                        background: cpuTone.background,
                        fontWeight: 600,
                        borderRadius: '999px',
                        padding: '2px 8px',
                    }}>
                        <Icon path={mdiChip} size="14px" />
                        {formatPercent(telemetry?.current?.cpu_percent)}
                    </span>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        color: memoryTone.color,
                        background: memoryTone.background,
                        fontWeight: 600,
                        borderRadius: '999px',
                        padding: '2px 8px',
                    }}>
                        <Icon path={mdiMemory} size="14px" />
                        {formatUsagePercent(telemetry?.current?.memory_used_bytes, telemetry?.current?.memory_total_bytes)}
                    </span>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        color: diskTone.color,
                        background: diskTone.background,
                        fontWeight: 600,
                        borderRadius: '999px',
                        padding: '2px 8px',
                    }}>
                        <Icon path={mdiHarddisk} size="14px" />
                        {formatUsagePercent(telemetry?.current?.disk_used_bytes, telemetry?.current?.disk_total_bytes)}
                    </span>
                </HBox>
                <span style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                    Uptime {formatUptime(telemetry?.current?.uptime_seconds)}
                    <span style={{ color: 'var(--fg-3)', padding: '0 6px' }}>•</span>
                    {collectedAtText}
                    {routerosVersion && (
                        <>
                            <span style={{ color: 'var(--fg-3)', padding: '0 6px' }}>•</span>
                            <span
                                title={routerosAntigo ? `Versão não suportada — o sincronismo exige RouterOS ${ROUTEROS_MIN.join('.')} ou superior` : undefined}
                                style={routerosAntigo ? {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '2px',
                                    color: 'var(--tc-color-orange-600)',
                                    fontWeight: 600,
                                } : undefined}
                            >
                                {routerosAntigo && <Icon path={mdiAlertCircleOutline} size="13px" />}
                                RouterOS {routerosVersion}
                            </span>
                        </>
                    )}
                </span>
            </VBox>
        );
    }, [telemetryMap]);

    const renderAgentCell = useCallback((row: ApplianceDTO) => {
        const applianceType = row.agent_type ?? 'mikrotik';
        const typeMeta = applianceTypeMeta[applianceType];

        if (!row.agent_id) {
            return (
                <HBox style={{ gap: '6px', alignItems: 'flex-start' }}>
                    <BrandIcon brand={applianceType} size={16} />
                    <span style={{ color: 'var(--tc-color-gray-400)', fontSize: '12px' }}>sem agente</span>
                </HBox>
            );
        }
        const agent = agentMap.get(row.agent_id);
        if (agent) {
            const agentTypeMismatch = agent.agentType !== applianceType;
            const squid = row.squidSummary?.ativo ? agent.squidStatus : undefined;
            const latestVersion = queryLatestVersion.data;
            const isOutdated = !!latestVersion && !!agent.version && agent.version !== latestVersion;
            const squidIcon = squid
                ? squid.status === 'active' ? mdiShieldCheck
                    : squid.status === 'inactive' ? mdiShieldOff
                        : squid.status === 'checking' ? mdiShieldSync
                            : mdiShieldAlert
                : row.squidSummary?.ativo ? mdiShieldSync : null;
            const squidColor = squid
                ? squid.status === 'active' ? 'var(--tc-color-success-600)'
                    : squid.status === 'inactive' ? 'var(--tc-color-danger-500)'
                        : squid.status === 'checking' ? 'var(--tc-color-warning-500)'
                            : 'var(--tc-color-danger-500)'
                : 'var(--tc-color-gray-400)';
            const squidTitle = squid
                ? `Squid: ${squid.status} (verificado: ${new Date(squid.checkedAt).toLocaleString()})`
                : row.squidSummary?.ativo ? 'Squid: aguardando primeira verificação' : undefined;
            // D-ipfix-limite: um roteador barulhento podia derrubar pacotes de
            // outros appliances no mesmo agente sem ninguém saber qual foi —
            // este ícone só existe pra tornar visível QUAL appliance está
            // sofrendo descarte agora, por minuto (não é um total histórico).
            const ipfixStats = row.id != null ? ipfixStatsMap.get(row.id) : undefined;
            const ipfixDropping = !!ipfixStats && ipfixStats.droppedPerMin > 0;
            const ipfixLimite = ipfixStats?.maxPps != null ? ` (limite ${ipfixStats.maxPps}/s)` : '';
            // Relógio do router desviado: os fluxos chegam e são gravados, mas
            // em janelas do passado (ou futuro) — o relatório fica vazio com o
            // coletor "verde". Um RB4011 sem NTP passou dois dias assim.
            const clockSkew = ipfixStats?.clockSkewSec ?? null;
            const ipfixClockOff = clockSkew != null && Math.abs(clockSkew) >= ROUTER_CLOCK_SKEW_WARN_SEC;
            const ipfixTitle = ipfixStats
                ? (ipfixDropping
                    ? `IPFIX: recebendo ${ipfixStats.receivedPerMin}/min, descartando ${ipfixStats.droppedPerMin}/min${ipfixLimite || ' (limite por appliance excedido)'}`
                    : `IPFIX: recebendo ${ipfixStats.receivedPerMin}/min, sem descarte${ipfixLimite}`)
                + (ipfixClockOff
                    ? `\nRelógio do router ${formatSkew(clockSkew)}: os fluxos estão sendo gravados fora da janela dos relatórios — verifique o NTP do appliance`
                    : '')
                : undefined;
            return (
                <VBox style={{ gap: '6px', alignItems: 'flex-start' }}>
                    <HBox style={{ alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <BrandIcon brand={applianceType} size={16} />
                        <span style={{ fontSize: '12px', color: 'var(--tc-color-success-700)', fontWeight: 500 }}
                            title={`Agente: ${agent.agentId}\nTipo reportado: ${agent.agentType}\nCapabilities: ${agent.capabilities.join(', ') || 'nenhuma'}\nConectado desde: ${new Date(agent.connectedAt).toLocaleString()}`}>
                            v{agent.version}
                        </span>
                        {agentTypeMismatch && (
                            <Icon
                                path={mdiShieldAlert}
                                size="16px"
                                color="var(--tc-color-danger-500)"
                                title={`Mismatch de tipo: appliance=${applianceType}, agente=${agent.agentType}`}
                            />
                        )}
                        {isOutdated && (
                            <Icon
                                path={mdiUpdate}
                                size="16px"
                                color="var(--tc-color-warning-600)"
                                title={`Atualização disponível: v${latestVersion}`}
                            />
                        )}
                        {squidIcon && (
                            <Icon path={squidIcon} size="16px" color={squidColor} title={squidTitle} />
                        )}
                        {ipfixStats && (
                            <Icon
                                path={ipfixClockOff ? mdiClockAlertOutline : ipfixDropping ? mdiAlertCircleOutline : mdiSwapHorizontal}
                                size="16px"
                                color={ipfixClockOff || ipfixDropping ? 'var(--tc-color-danger-500)' : 'var(--tc-color-gray-400)'}
                                title={ipfixTitle}
                            />
                        )}
                    </HBox>
                </VBox>
            );
        }
        return (
            <VBox style={{ gap: '6px', alignItems: 'flex-start' }}>
                <HBox style={{ alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <Icon path={mdiRobotOffOutline} size="18px" color="var(--tc-color-gray-400)" />
                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)' }}
                        title={`ID: ${row.agent_id} (desconectado)`}>
                        offline
                    </span>
                </HBox>
                <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    borderRadius: '999px',
                    padding: '2px 8px',
                    color: typeMeta.color,
                    background: typeMeta.background,
                }}>
                    {typeMeta.label}
                </span>
            </VBox>
        );
    }, [agentMap, queryLatestVersion.data, ipfixStatsMap]);

    const renderActionsCell = useCallback((row: ApplianceDTO) => {
        const rowId = row.id;
        const canRequestAgentUpdate = !!row.agent_id && rowId != null && agentMap.has(row.agent_id) && agentMap.get(row.agent_id)?.installType === 'bare-metal';

        return (
            <HBox style={{ gap: '8px', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: isMobile ? 'flex-start' : undefined }}>
                {rowId != null && (
                    <Botao
                        size="sm"
                        variant="outline-secondary"
                        icon={mdiChartLine}
                        onClick={() => setTelemetryAppliance(row)}
                        title="Abrir telemetria"
                    >
                    </Botao>
                )}

                <Botao
                    size='sm'
                    variant="outline-primary"
                    icon={mdiPencil}
                    onClick={() => handleOpenModal(row)}
                    title="Editar"
                />
                {rowId != null && (
                    <BotaoExcluirComCascata
                        tipo="appliance"
                        id={rowId}
                        variant="outline-danger"
                        title="Excluir"
                        onExcluido={() => {
                            queryList.refetch();
                            toastSuccess('Appliance removida com sucesso!');
                        }}
                        onErro={(msg) => toastError(msg)}
                    />
                )}
                <BotaoDropdown
                    size='sm'
                    variant="outline-secondary"
                    icon={mdiDotsVertical}
                    noArrow
                    position='auto'
                    title="Mais ações"
                >
                    <VBox style={{ gap: '2px' }}>
                        {([{ icon: mdiHistory, label: 'Histórico de sincronização', onClick: () => setSyncLogAppliance(row), disabled: false },
                        { icon: mdiFlaskOutline, label: 'Simular sincronização (dry run)', onClick: () => handleDryRun(row), disabled: false },
                        { icon: mdiLanConnect, label: 'Diagnóstico de rede', onClick: () => setDiagnosticsAppliance(row), disabled: false },
                        {
                            icon: mdiPackageUp,
                            label: row.agent_type === 'opnsense' ? 'Atualizar OPNsense' : 'Atualizar RouterOS',
                            onClick: () => setUpgradeAppliance(row),
                            disabled: false,
                        },
                        {
                            icon: mdiUpdate,
                            label: row.agent_id && agentMap.get(row.agent_id)?.installType === 'bare-metal'
                                ? 'Atualizar Agente'
                                : agentMap.get(row.agent_id!)?.installType
                                    ? `Atualizar Agente (${agentMap.get(row.agent_id!)!.installType} — manual)`
                                    : 'Atualizar Agente',
                            onClick: () => canRequestAgentUpdate
                                ? mutationAgentUpdate.mutate(rowId)
                                : undefined,
                            disabled: !canRequestAgentUpdate,
                        },
                        ] as const).map(item => (
                            <button
                                key={item.label}
                                type="button"
                                onClick={item.onClick}
                                disabled={item.disabled}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '8px', borderRadius: '8px',
                                    background: 'none', border: 'none',
                                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                                    color: item.disabled ? 'var(--tc-color-gray-400)' : 'var(--tc-color-gray-700)',
                                    width: '100%',
                                    textAlign: 'left', fontSize: '14px', whiteSpace: 'nowrap',
                                    opacity: item.disabled ? 0.5 : 1,
                                }}
                                onMouseEnter={e => { if (!item.disabled) e.currentTarget.style.backgroundColor = 'var(--tc-color-gray-200)'; }}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <Icon path={item.icon} size="18px" />
                                {item.label}
                            </button>
                        ))}
                    </VBox>
                </BotaoDropdown>
            </HBox>
        );
    }, [agentMap, handleDelete, handleDryRun, handleOpenModal, isMobile, mutationAgentUpdate]);

    // Badge de atualização do sistema: em execução chama atenção (a rede vai
    // cair/está fora); agendada é só informativa. Clicar abre o modal.
    const renderUpgradeBadge = (row: ApplianceDTO) => {
        const upgrade = row.id != null ? upgradeMap.get(row.id) : undefined;
        if (!upgrade) return null;
        const executando = upgrade.status !== 'agendado';
        const quando = new Date(upgrade.agendado_para).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
        return (
            <button
                type="button"
                onClick={() => setUpgradeAppliance(row)}
                title={executando ? (upgrade.etapa ?? 'Atualizando') : `Atualização agendada · ${quando} · Começa até ${upgrade.tolerancia_min} min depois do horário agendado`}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', alignSelf: 'flex-start' }}
            >
                <Badge variant={executando ? 'warning' : 'info'}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Icon path={executando ? mdiLoading : mdiPackageUp} spin={executando} size="14px" />
                        {executando
                            ? `${upgrade.status === 'reiniciando' ? 'Reiniciando' : 'Atualizando'} ${row.agent_type === 'opnsense' ? 'OPNsense' : 'RouterOS'} → ${upgrade.versao_alvo}`
                            : `${quando}`}
                    </span>
                </Badge>
            </button>
        );
    };

    const headers: DataTableHeader[] = [
        {
            column: 'nome',
            label: 'Nome',
            mobileOrder: 0,
            render: (row: ApplianceDTO) => {
                const telemetry = row.id != null ? telemetryMap.get(row.id) : undefined;
                const status = telemetry?.status ?? 'unknown';
                const statusMeta = telemetryStatusMeta[status];

                return (
                    <VBox style={{ gap: '4px' }}>
                        <HBox style={{ gap: '8px', alignItems: 'center' }}>
                            <span
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: statusMeta.color,
                                    display: 'inline-block',
                                    flexShrink: 0,
                                }}
                                title={statusMeta.label}
                            />
                            <span>{row.nome}</span>
                        </HBox>
                        {renderUpgradeBadge(row)}
                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                            {row.descricao}
                        </span>
                    </VBox>
                );
            },
        },
        {
            column: 'enderecos',
            label: 'Endereço IP',
            mobileLabel: 'Rede',
            mobileOrder: 1,
            render: (row: ApplianceDTO) => {
                return (
                    <VBox style={{ gap: '4px' }}>
                        <span>{row.enderecos?.[0] || '-'}</span>
                        <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                            <span>{row.redesLan?.length || 0} LAN{row.redesLan && row.redesLan.length !== 1 ? 's' : ''}, {row.redesWan?.length || 0} WAN{row.redesWan && row.redesWan.length !== 1 ? 's' : ''}</span>
                        </span>
                    </VBox>
                );
            },
        },
        {
            column: 'agente',
            label: 'Agente',
            mobileOrder: 2,
            render: renderAgentCell,
        },
        {
            column: 'telemetria',
            label: 'Telemetria',
            width: '260px',
            mobileOrder: 3,
            render: renderTelemetryCell,
        },
        {
            column: 'patches',
            label: 'Patches',
            mobileOrder: 5,
            render: renderPatchesCell,
        },
        {
            column: 'acoes',
            label: 'Ações',
            mobileOrder: 4,
            render: renderActionsCell,
            mobileRender: renderActionsCell,
        },
    ];

    return (
        <LayoutUsuario>
            <VBox style={{ padding: isMobile ? '16px' : '24px', gap: '16px', height: '100%', overflow: 'hidden' }}>
                <PageHeader
                    title="Appliances"
                    description="Gerencie os roteadores do sistema"
                    actions={(
                        <>
                            <Botao
                                icon={mdiDownload}
                                onClick={() => setAgentDownloadOpen(true)}
                                variant="outline-secondary"
                                size="md"
                                title="Download manual do agente (avançado)"
                                style={{ flex: isMobile ? '1 1 160px' : undefined }}
                            >
                                Agente
                            </Botao>
                            <Botao
                                icon={mdiPlus}
                                onClick={() => handleOpenModal()}
                                variant="outline-secondary"
                                size="md"
                                title="Cadastro manual, sem agente"
                                style={{ flex: isMobile ? '1 1 160px' : undefined }}
                            >
                                Adicionar
                            </Botao>
                            <Botao
                                icon={mdiRouterWireless}
                                onClick={() => setInstallAgentOpen(true)}
                                variant="primary"
                                size="md"
                                style={{ flex: isMobile ? '1 1 160px' : undefined }}
                            >
                                Instalar Agente
                            </Botao>
                        </>
                    )}
                />

                <HBox style={{ marginTop: isMobile ? '8px' : '24px', width: '100%' }}>
                    <CampoTexto
                        value={search}
                        onChange={setSearch}
                        placeholder="Buscar appliances..."
                        icon={mdiMagnify}
                        style={{ flex: 1 }}
                    />
                </HBox>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '140px' : '180px'}, 1fr))`,
                        gap: '12px',
                        width: '100%',
                    }}
                >
                    {([
                        { key: 'online', label: 'Online', value: telemetryTotals.online, color: 'var(--telemetry-summary-online-color)', background: 'var(--telemetry-summary-online-bg)' },
                        { key: 'degraded', label: 'Degradadas', value: telemetryTotals.degraded, color: 'var(--telemetry-summary-degraded-color)', background: 'var(--telemetry-summary-degraded-bg)' },
                        { key: 'unknown', label: 'Sem coleta', value: telemetryTotals.unknown, color: 'var(--telemetry-summary-unknown-color)', background: 'var(--telemetry-summary-unknown-bg)' },
                        { key: 'offline', label: 'Offline', value: telemetryTotals.offline, color: 'var(--telemetry-summary-offline-color)', background: 'var(--telemetry-summary-offline-bg)' },
                    ] as const).map((item) => (
                        <VBox
                            key={item.key}
                            style={{
                                minWidth: 0,
                                padding: '12px 14px',
                                borderRadius: '12px',
                                border: '1px solid var(--border-1)',
                                background: item.background,
                                gap: '4px',
                            }}
                        >
                            <span style={{ fontSize: '12px', fontWeight: 600, color: item.color }}>{item.label}</span>
                            <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--color-body)' }}>{item.value}</span>
                        </VBox>
                    ))}
                </div>

                {queryList.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <Icon path={mdiLoading} spin size={2} />
                    </HBox>
                )}

                {queryList.isError && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <p>Erro ao carregar appliances. Por favor, tente novamente.</p>
                    </HBox>
                )}

                {!queryList.isLoading && !queryList.isError && queryList.data && (
                    <DataTable
                        cabecalhos={headers}
                        linhas={
                            queryList.data?.pages?.flatMap(p => p.data.result ?? []) ?? []
                        }
                        onLoadMore={() => {
                            if (queryList.hasNextPage && !queryList.isFetchingNextPage) {
                                queryList.fetchNextPage();
                            }
                        }}
                        hasMoreData={queryList.hasNextPage}
                        isLoading={queryList.isFetchingNextPage}
                    />
                )}
            </VBox>

            <Modal
                show={isModalOpen}
                title={editingId ? 'Editar Appliance' : 'Nova Appliance'}
                onClose={handleCloseModal}
                onCancel={handleCloseModal}
                onConfirm={handleSave}
                confirmProps={{
                    label: 'Salvar',
                    disabled: mutSave.isLoading,
                }}
                cancelProps={{
                    label: 'Cancelar',
                }}
                size="md"
            >
                {model && (
                    <FormAppliance
                        value={model}
                        onChange={setModel}
                        fieldErrors={fieldErrors}
                    />
                )}
            </Modal>

            {syncLogAppliance && (
                <SyncLogModal
                    applianceId={syncLogAppliance.id!}
                    applianceNome={syncLogAppliance.nome}
                    show={syncLogAppliance !== null}
                    onClose={() => setSyncLogAppliance(null)}
                />
            )}

            <DryRunModal
                show={dryRunAppliance !== null}
                applianceNome={dryRunAppliance?.nome}
                loading={dryRunLoading}
                acoes={dryRunAcoes}
                erro={dryRunErro}
                onClose={() => setDryRunAppliance(null)}
            />
            <DiagnosticsModal
                appliance={diagnosticsAppliance}
                onClose={() => setDiagnosticsAppliance(null)}
            />
            <UpgradeModal
                appliance={upgradeAppliance}
                onClose={() => {
                    setUpgradeAppliance(null);
                    // O resumo de patches muda quando um upgrade termina.
                    queryOsPatchesAppliances.refetch();
                    queryUpgradesAtivos.refetch();
                }}
            />
            <AgentDownloadModal
                show={agentDownloadOpen}
                onClose={() => setAgentDownloadOpen(false)}
            />
            <InstallAgentModal
                show={installAgentOpen}
                onClose={() => setInstallAgentOpen(false)}
                onAgentConnected={({ agentId, vendor, endereco, nome }) => {
                    // Agente já conectado (e, se veio de um candidato MNDP,
                    // já com IP e um nome sugerido) — falta só a credencial de
                    // acesso e confirmar o cadastro. Reaproveita o formulário
                    // manual de sempre, só que pré-preenchido: o operador não
                    // configura o adapter de novo, e o mesmo agente reaproveita
                    // token/segredos (ver findAppliancesByAgentId no backend).
                    handleOpenModal({
                        agent_type: vendor,
                        agent_id: agentId,
                        nome: nome ?? '',
                        descricao: '',
                        enderecos: [endereco ?? ''],
                        usuario: '',
                        senha: '',
                        snmp_enabled: false,
                        snmp_version: '2c',
                        snmp_port: 161,
                        snmp_community: '',
                        redesLan: [],
                        redesWan: [],
                        ipsecs: [],
                        dispositivos: [],
                        portas: [],
                    } as ApplianceDTO);
                }}
            />

            <TelemetryOverviewModal
                show={telemetryAppliance !== null}
                title={telemetryAppliance ? `Telemetria: ${telemetryAppliance.nome}` : 'Telemetria'}
                onClose={() => setTelemetryAppliance(null)}
                overviewUrl={(period) => `/api/telemetry/appliances/${telemetryAppliance!.id}/overview?window=${period}`}
                errorMessage="Erro ao carregar visão de telemetria da appliance."
            />
        </LayoutUsuario>
    );
};

export default Page;
